import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_PATHS } from '../config/api-paths';
import {
  BlacklistAddRequest,
  BlacklistEntry,
  BlacklistFilters,
  LenderBlacklistApi,
} from '../tokens/lender-blacklist.token';

/** B1 list envelope — the resource wraps the rows in {@code { data: [...] }}. */
interface BlacklistListEnvelope {
  data: BlacklistEntry[];
}

/**
 * REST-only implementation of {@link LenderBlacklistApi} (EM-699 / F5 — EM-710).
 *
 * Thin {@code HttpClient} wrapper over the live {@code LenderBlacklistResource}
 * (EM-703 / B4) at {@code /services/embedded/api/v1/lender/blacklist}. No mock,
 * no fallback (Rules 07/10); every entry is lender-scoped server-side (Rule 22).
 * Rides the Keycloak bearer via {@code authInterceptor}. The single home for the
 * blacklist endpoints — consumed by the {@code features/lender-sm/blacklist/} page
 * through the {@link LENDER_BLACKLIST} DI token.
 */
@Injectable()
export class LenderBlacklistRestService implements LenderBlacklistApi {
  private readonly http = inject(HttpClient);

  list(filters?: BlacklistFilters): Observable<BlacklistEntry[]> {
    let params = new HttpParams();
    if (filters?.q) params = params.set('q', filters.q);
    if (filters?.type) params = params.set('type', filters.type);
    if (filters?.status) params = params.set('status', filters.status);
    return this.http
      .get<BlacklistListEnvelope>(API_PATHS.LENDER_BLACKLIST, { params })
      .pipe(map(env => env?.data ?? []));
  }

  add(request: BlacklistAddRequest): Observable<BlacklistEntry> {
    return this.http.post<BlacklistEntry>(API_PATHS.LENDER_BLACKLIST, request);
  }

  softRemove(id: number): Observable<BlacklistEntry> {
    return this.http.delete<BlacklistEntry>(`${API_PATHS.LENDER_BLACKLIST}/${id}`);
  }

  restore(id: number): Observable<BlacklistEntry> {
    return this.http.post<BlacklistEntry>(`${API_PATHS.LENDER_BLACKLIST}/${id}/restore`, {});
  }
}
